import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, Clock, Home, ExternalLink, BookOpen, Plane, Moon, RefreshCw,
  HelpCircle, CheckCircle, ChevronDown, ChevronUp, Users, TrendingUp,
  TrendingDown, Minus, ShieldAlert, CalendarClock, Timer, Palmtree,
  AlertCircle, UserX, ClockAlert, ArrowRight, CalendarDays, Building2,
} from 'lucide-react';
import { formatDate, formatTimeString12Hour, formatHoursMinutes } from '../../utils/dateUtils';
import { getBurnoutPriorityScore } from './burnoutUtils';
import { CountBadge } from '../UI/CountBadge';
import AccordionSection from '../UI/AccordionSection';
import PillToggle from '../UI/PillToggle';
import ComplianceWatchlist, {
  isOtWatchlistMember,
  isRestDayWatchlistMember,
} from './ComplianceWatchlist';

// ---------------------------------------------------------------------------
// Composite status configuration
// ---------------------------------------------------------------------------

const STATUS_CONFIG = {
  on_site: {
    label: 'Present',
    badge: 'bg-green-50 text-green-700 ring-1 ring-green-600/20',
    dot: 'bg-green-500',
    icon: CheckCircle,
  },
  work_from_home: {
    label: 'WFH',
    badge: 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20',
    dot: 'bg-blue-500',
    icon: Home,
  },
  external_activity: {
    label: 'External',
    badge: 'bg-purple-50 text-purple-700 ring-1 ring-purple-600/20',
    dot: 'bg-purple-500',
    icon: ExternalLink,
  },
  on_course: {
    label: 'On Course',
    badge: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600/20',
    dot: 'bg-indigo-500',
    icon: BookOpen,
  },
  on_leave: {
    label: 'On Leave',
    badge: 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20',
    dot: 'bg-amber-500',
    icon: Plane,
  },
  nightwork: {
    label: 'Nightwork',
    badge: 'bg-slate-50 text-slate-700 ring-1 ring-slate-600/20',
    dot: 'bg-slate-500',
    icon: Moon,
  },
  nightwork_rest_day: {
    label: 'Recovery',
    badge: 'bg-teal-50 text-teal-700 ring-1 ring-teal-600/20',
    dot: 'bg-teal-500',
    icon: RefreshCw,
  },
  unaccounted: {
    label: 'Unaccounted',
    badge: 'bg-red-50 text-red-700 ring-1 ring-red-600/20',
    dot: 'bg-red-500',
    icon: HelpCircle,
  },
  non_working_day: {
    label: 'Off Day',
    badge: 'bg-gray-50 text-gray-500 ring-1 ring-gray-400/20',
    dot: 'bg-gray-300',
    icon: null,
  },
  public_holiday: {
    label: 'Public Holiday',
    badge: 'bg-orange-50 text-orange-700 ring-1 ring-orange-600/20',
    dot: 'bg-orange-400',
    icon: null,
  },
};

const COMPLIANCE_STYLES = {
  watch:         'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20',
  warning:       'bg-orange-50 text-orange-700 ring-1 ring-orange-600/20',
  at_risk:       'bg-orange-50 text-orange-700 ring-1 ring-orange-600/20',
  critical:      'bg-red-50 text-red-700 ring-1 ring-red-600/20',
  non_compliant: 'bg-red-50 text-red-700 ring-1 ring-red-600/20',
};

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

const LEAVE_ABBREVIATION_FALLBACK = {
  'Annual Leave': 'AL',
  'Sick Leave': 'SL',
  'Outpatient Sick Leave': 'OSL',
  'Medical Leave': 'MC',
  'Hospitalisation Leave': 'HL',
  'Maternity Leave': 'ML',
  'Paternity Leave': 'PL',
  'Adoption Leave': 'ADL',
  'Childcare Leave': 'CCL',
  'Extended Childcare Leave': 'ECCL',
  'Shared Parental Leave': 'SPL',
  'Unpaid Infant Care Leave': 'UICL',
  'National Service Leave': 'NSL',
  'Compassionate Leave (Immediate Family)': 'CL',
  'Compassionate Leave (Grandparent)': 'CL',
  'Marriage Leave': 'MRL',
  'Study Leave': 'STL',
  'Unpaid Leave': 'UL',
  'Urgent Leave': 'UGL',
  'Home Leave': 'HML',
  'Jury Service Leave': 'JSL',
  'Off in Lieu': 'OIL',
};

function StatusBadge({ status, leaveTypeAbbr, leaveTypeName }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unaccounted;
  let displayLabel = cfg.label;
  if (status === 'on_leave' && (leaveTypeAbbr || leaveTypeName)) {
    displayLabel = leaveTypeAbbr || LEAVE_ABBREVIATION_FALLBACK[leaveTypeName] || cfg.label;
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {displayLabel}
    </span>
  );
}

function ComplianceChip({ label, severity }) {
  const classes = COMPLIANCE_STYLES[severity];
  if (!classes) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
      {label}
    </span>
  );
}

function TrendArrow({ current, previous, higherIsBetter = true }) {
  if (current == null || previous == null) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.01) return <Minus className="w-3.5 h-3.5 text-gray-400" />;
  const isUp = diff > 0;
  const isGood = higherIsBetter ? isUp : !isUp;
  const Icon = isUp ? TrendingUp : TrendingDown;
  return <Icon className={`w-3.5 h-3.5 ${isGood ? 'text-green-600' : 'text-red-500'}`} />;
}

// ---------------------------------------------------------------------------
// 1. Action Center — alert banner for items needing RO attention
// ---------------------------------------------------------------------------

function ActionCenter({ members }) {
  const alerts = useMemo(() => {
    const items = [];
    const unaccounted = members.filter((m) => m.composite_status === 'unaccounted');
    if (unaccounted.length > 0) {
      items.push({
        key: 'unaccounted',
        icon: UserX,
        severity: 'bg-red-50 border-red-200 text-red-800',
        iconColor: 'text-red-600',
        text: `${unaccounted.length} unaccounted employee${unaccounted.length > 1 ? 's' : ''} — no clock-in or leave recorded`,
      });
    }
    const missingClockout = members.filter((m) => m.attendance_exception === 'missing_clockout');
    if (missingClockout.length > 0) {
      items.push({
        key: 'missing_clockout',
        icon: ClockAlert,
        severity: 'bg-orange-50 border-orange-200 text-orange-800',
        iconColor: 'text-orange-600',
        text: `${missingClockout.length} missing clock-out${missingClockout.length > 1 ? 's' : ''}`,
      });
    }
    const pendingLeave = members.filter((m) => m.pending_leave_count > 0);
    if (pendingLeave.length > 0) {
      const total = pendingLeave.reduce((s, m) => s + m.pending_leave_count, 0);
      items.push({
        key: 'pending_leave',
        icon: CalendarClock,
        severity: 'bg-amber-50 border-amber-200 text-amber-800',
        iconColor: 'text-amber-600',
        text: `${total} pending leave application${total > 1 ? 's' : ''} awaiting approval`,
      });
    }
    const complianceAlerts = members.filter((m) => {
      const isWorker = m.classification === 'worker';
      return (
        (m.rest_day_compliance && m.rest_day_compliance !== 'compliant') ||
        (isWorker && m.ot_compliance && m.ot_compliance !== 'compliant')
      );
    });
    if (complianceAlerts.length > 0) {
      items.push({
        key: 'compliance',
        icon: ShieldAlert,
        severity: 'bg-orange-50 border-orange-200 text-orange-800',
        iconColor: 'text-orange-600',
        text: `${complianceAlerts.length} compliance alert${complianceAlerts.length > 1 ? 's' : ''} (rest day / OT)`,
      });
    }
    return items;
  }, [members]);

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-2">
      {alerts.map(({ key, icon: Icon, severity, iconColor, text }) => (
        <div key={key} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${severity}`}>
          <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
          <span className="text-sm font-medium">{text}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. KPI Cards — 4 core metrics for the RO
// ---------------------------------------------------------------------------

function KPICards({ members, trendsData, hasWorkers }) {
  const totalMembers = members.length;

  const { attendanceRate, avgEffectiveHours, otRiskCount, restDayRiskCount } = useMemo(() => {
    const working = members.filter(
      (m) => !['non_working_day', 'public_holiday'].includes(m.composite_status)
    );
    const present = members.filter((m) =>
      ['on_site', 'work_from_home', 'external_activity', 'on_course', 'nightwork'].includes(m.composite_status)
    );
    const rate = working.length > 0 ? (present.length / working.length) * 100 : null;

    const hoursValues = members
      .map((m) => m.effective_hours)
      .filter((h) => h != null);
    const avgHours = hoursValues.length > 0
      ? hoursValues.reduce((s, h) => s + h, 0) / hoursValues.length
      : null;

    const otCount = members.filter(isOtWatchlistMember).length;
    const restCount = members.filter(isRestDayWatchlistMember).length;

    return {
      attendanceRate: rate,
      avgEffectiveHours: avgHours,
      otRiskCount: otCount,
      restDayRiskCount: restCount,
    };
  }, [members]);

  const prevWeekData = trendsData?.weeks?.length >= 2
    ? trendsData.weeks[trendsData.weeks.length - 2]
    : null;

  const prevAvgHours = (() => {
    if (!prevWeekData) return null;
    const staff = prevWeekData.by_classification?.staff?.avg_daily_effective_hours;
    const worker = prevWeekData.by_classification?.worker?.avg_daily_effective_hours;
    if (staff != null && worker != null) return (staff + worker) / 2;
    return staff ?? worker ?? null;
  })();

  const cards = [
    {
      label: 'Attendance Rate',
      value: attendanceRate != null ? `${Math.round(attendanceRate)}%` : '—',
      icon: Users,
      bg: 'bg-green-50',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-700',
      sub: `${totalMembers} total`,
    },
    {
      label: 'Avg Effective Hours',
      value: avgEffectiveHours != null ? formatHoursMinutes(avgEffectiveHours) : '—',
      icon: Timer,
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-700',
      trend: <TrendArrow current={avgEffectiveHours} previous={prevAvgHours} higherIsBetter={false} />,
      sub: 'today',
    },
  ];

  // MOM compliance KPIs only surface when the team includes workers.
  // Staff-only managers (e.g. admin ROs) would otherwise see perpetual
  // "0 / all clear" cards for regulations that don't apply to their
  // reports — that's noise, not signal.
  if (hasWorkers) {
    cards.push(
      {
        label: '72h OT',
        value: otRiskCount,
        icon: ShieldAlert,
        bg: otRiskCount > 0 ? 'bg-orange-50' : 'bg-gray-50',
        iconBg: otRiskCount > 0 ? 'bg-orange-100' : 'bg-gray-100',
        iconColor: otRiskCount > 0 ? 'text-orange-600' : 'text-gray-500',
        sub: otRiskCount > 0
          ? `worker${otRiskCount > 1 ? 's' : ''} at risk`
          : 'all clear',
      },
      {
        label: 'Rest Day',
        value: restDayRiskCount,
        icon: CalendarDays,
        bg: restDayRiskCount > 0 ? 'bg-red-50' : 'bg-gray-50',
        iconBg: restDayRiskCount > 0 ? 'bg-red-100' : 'bg-gray-100',
        iconColor: restDayRiskCount > 0 ? 'text-red-600' : 'text-gray-500',
        sub: restDayRiskCount > 0
          ? `near 12-day limit`
          : 'all clear',
      },
    );
  }

  // 2 universal KPIs → 2-col; 4 KPIs (with compliance) → 2-col mobile, 4-col desktop
  const gridCols = cards.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-4';

  return (
    <div className={`grid ${gridCols} gap-3`}>
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} className="bg-white rounded-2xl shadow-sm border p-5">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${c.iconBg}`}>
                <Icon className={`w-5 h-5 ${c.iconColor}`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xl font-bold text-gray-900">{c.value}</span>
                  {c.trend}
                </div>
                <p className="text-xs text-gray-500 truncate">{c.label}</p>
              </div>
            </div>
            {c.sub && <p className="text-[10px] text-gray-400 mt-2 ml-[52px]">{c.sub}</p>}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Team Status Bar — compact distribution strip
// ---------------------------------------------------------------------------

function TeamStatusBar({ summary, total }) {
  const statusOrder = [
    'on_site', 'work_from_home', 'external_activity', 'on_course',
    'nightwork', 'on_leave', 'nightwork_rest_day', 'unaccounted',
  ];

  const entries = statusOrder
    .map((s) => ({ key: s, count: summary[s] || 0, cfg: STATUS_CONFIG[s] }))
    .filter((e) => e.count > 0);

  if (!entries.length) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4">
      <div className="flex items-center gap-1.5 h-3 rounded-full overflow-hidden mb-3">
        {entries.map(({ key, count, cfg }) => (
          <div
            key={key}
            className="h-full rounded-full transition-all"
            style={{
              width: `${Math.max((count / total) * 100, 3)}%`,
              backgroundColor: cfg.dot.replace('bg-', '').includes('green') ? '#22c55e'
                : cfg.dot.includes('blue') ? '#3b82f6'
                : cfg.dot.includes('purple') ? '#a855f7'
                : cfg.dot.includes('indigo') ? '#6366f1'
                : cfg.dot.includes('amber') ? '#f59e0b'
                : cfg.dot.includes('slate') ? '#64748b'
                : cfg.dot.includes('teal') ? '#14b8a6'
                : cfg.dot.includes('red') ? '#ef4444'
                : '#d1d5db',
            }}
            title={`${cfg.label}: ${count}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {entries.map(({ key, count, cfg }) => (
          <div key={key} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
            <span>{cfg.label}</span>
            <span className="font-semibold text-gray-900">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Wellbeing Flags — leave-based wellbeing signals
// ---------------------------------------------------------------------------

function WellbeingFlags({ leaveInsightsData, trendsData }) {
  const memberInsights = leaveInsightsData?.members ?? [];
  const memberHours = trendsData?.member_hours_summary ?? [];

  const leaveByStaffId = useMemo(
    () => new Map(memberInsights.map((member) => [member.staff_id, member])),
    [memberInsights],
  );

  const flags = useMemo(() => {
    const previewNames = (list, sorter = (a, b) => a.full_name.localeCompare(b.full_name)) => {
      const sorted = [...list].sort(sorter);
      const names = sorted.slice(0, 2).map((m) => m.full_name);
      if (!names.length) return '';
      if (sorted.length <= 2) return names.join(', ');
      return `${names.join(', ')} +${sorted.length - 2} more`;
    };

    const items = [];
    const zeroAL = memberInsights.filter(
      (m) => (m.al_utilisation_rate ?? null) === 0 && (m.al_remaining_days ?? 0) > 0,
    );
    if (zeroAL.length > 0) {
      items.push({
        key: 'zero_al',
        label: 'Zero AL Utilisation',
        count: zeroAL.length,
        color: 'text-amber-700 bg-amber-50 ring-amber-600/20',
        icon: Palmtree,
        description: `${zeroAL.length} employee${zeroAL.length > 1 ? 's have' : ' has'} not taken any annual leave this year`,
        names: previewNames(zeroAL),
      });
    }
    const highMC = memberInsights.filter((m) => (m.mc_days_ytd ?? 0) >= 4);
    if (highMC.length > 0) {
      items.push({
        key: 'high_mc',
        label: 'Frequent MC',
        count: highMC.length,
        color: 'text-orange-700 bg-orange-50 ring-orange-600/20',
        icon: AlertTriangle,
        description: `${highMC.length} employee${highMC.length > 1 ? 's' : ''} with 4+ MC occurrences`,
        names: previewNames(highMC),
      });
    }
    const monFriMC = memberInsights.filter((m) => m.mon_fri_mc_flag);
    if (monFriMC.length > 0) {
      items.push({
        key: 'mon_fri_mc',
        label: 'Mon/Fri MC Pattern',
        count: monFriMC.length,
        color: 'text-red-700 bg-red-50 ring-red-600/20',
        icon: ShieldAlert,
        description: `${monFriMC.length} employee${monFriMC.length > 1 ? 's' : ''} flagged for Mon/Fri MC clustering`,
        names: previewNames(monFriMC),
      });
    }
    const longGap = memberInsights.filter((m) => (m.days_since_last_leave ?? 0) >= 60);
    if (longGap.length > 0) {
      items.push({
        key: 'long_gap',
        label: 'Long Leave Gap',
        count: longGap.length,
        color: 'text-blue-700 bg-blue-50 ring-blue-600/20',
        icon: CalendarClock,
        description: `${longGap.length} employee${longGap.length > 1 ? 's have' : ' has'} not taken leave in 60+ days`,
        names: previewNames(longGap, (a, b) => (b.days_since_last_leave ?? 0) - (a.days_since_last_leave ?? 0)),
      });
    }

    const chronicExtended = memberHours.filter(
      (m) => m.classification === 'staff' && (m.extended_days_rate ?? 0) >= 0.6,
    );
    if (chronicExtended.length > 0) {
      items.push({
        key: 'chronic_extended',
        label: 'Chronic Extended Hours',
        count: chronicExtended.length,
        color: 'text-amber-700 bg-amber-50 ring-amber-600/20',
        icon: Clock,
        description: `${chronicExtended.length} staff member${chronicExtended.length > 1 ? 's' : ''} worked past schedule on 60%+ of tracked days`,
        names: previewNames(chronicExtended, (a, b) => getBurnoutPriorityScore(b) - getBurnoutPriorityScore(a)),
      });
    }

    const consistentLongDays = memberHours.filter((m) => {
      const threshold = m.classification === 'worker' ? 11 : 10;
      return (m.avg_daily_effective_hours ?? 0) >= threshold;
    });
    if (consistentLongDays.length > 0) {
      items.push({
        key: 'consistent_long_days',
        label: 'Consistently Long Days',
        count: consistentLongDays.length,
        color: 'text-red-700 bg-red-50 ring-red-600/20',
        icon: AlertTriangle,
        description: `${consistentLongDays.length} employee${consistentLongDays.length > 1 ? 's average' : ' averages'} long workdays across the trend window`,
        names: previewNames(consistentLongDays, (a, b) => getBurnoutPriorityScore(b) - getBurnoutPriorityScore(a)),
      });
    }

    const noRestDays = memberHours.filter((m) => (m.consecutive_working_days ?? 0) >= 12);
    if (noRestDays.length > 0) {
      items.push({
        key: 'no_rest_days',
        label: 'No Rest Days',
        count: noRestDays.length,
        color: 'text-red-700 bg-red-50 ring-red-600/20',
        icon: ShieldAlert,
        description: `${noRestDays.length} employee${noRestDays.length > 1 ? 's have' : ' has'} 12+ consecutive working days`,
        names: previewNames(noRestDays, (a, b) => (b.consecutive_working_days ?? 0) - (a.consecutive_working_days ?? 0)),
      });
    }

    const compoundRisk = memberHours.filter((m) => {
      const leaveRow = leaveByStaffId.get(m.staff_id);
      return (
        (m.avg_daily_effective_hours ?? 0) >= 9.5 &&
        (leaveRow?.days_since_last_leave ?? 0) >= 60
      );
    });
    if (compoundRisk.length > 0) {
      items.push({
        key: 'compound_risk',
        label: 'Long Hours + No Recent Leave',
        count: compoundRisk.length,
        color: 'text-red-700 bg-red-50 ring-red-600/20',
        icon: AlertCircle,
        description: `${compoundRisk.length} employee${compoundRisk.length > 1 ? 's are' : ' is'} combining long hours with a 60+ day leave gap`,
        names: previewNames(compoundRisk, (a, b) => getBurnoutPriorityScore(b) - getBurnoutPriorityScore(a)),
      });
    }
    return items;
  }, [leaveByStaffId, memberHours, memberInsights]);

  if (flags.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-5 py-3 border-b bg-gray-50 rounded-t-2xl">
        <h4 className="text-sm font-semibold text-gray-700">Wellbeing Signals</h4>
      </div>
      <div className="p-4 space-y-3">
        {flags.map(({ key, label, count, color, icon: Icon, description, names }) => (
          <div key={key} className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${color} ring-1`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{label}</span>
                <CountBadge count={count} theme="employee" />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{description}</p>
              {names && <p className="text-[11px] text-gray-400 mt-1 truncate">{names}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5. Upcoming Leave Panel
// ---------------------------------------------------------------------------

function UpcomingLeavePanel({ upcomingLeaveData }) {
  const items = upcomingLeaveData?.items ?? [];
  const rangeEnd = upcomingLeaveData?.range_end;
  const grouped = useMemo(() => {
    const map = new Map();
    for (const item of items) {
      const key = item.start_date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">Upcoming Leave</h4>
            {rangeEnd && <span className="text-xs text-gray-400">Next 7 days</span>}
          </div>
        </div>
        <div className="px-5 py-6 text-center text-sm text-gray-400">
          No upcoming leave in the next 7 days
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-5 py-3 border-b bg-gray-50 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-gray-700">Upcoming Leave</h4>
          <CountBadge count={items.length} theme="employee" size="md" />
        </div>
      </div>
      <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
        {grouped.map(([dateStr, leaves]) => (
          <div key={dateStr} className="p-3 border border-gray-200 rounded-xl">
            <p className="text-xs font-semibold text-gray-500 mb-2">
              {formatDate(dateStr)}
              {leaves.length > 1 && (
                <span className="ml-1.5 text-amber-600 font-medium">
                  ({leaves.length} on leave)
                </span>
              )}
            </p>
            <div className="space-y-1.5">
              {leaves.map((l, idx) => (
                <div key={`${l.staff_id}-${idx}`} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-800 truncate">{l.full_name}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-gray-500">{l.leave_type}</span>
                    {l.total_days != null && (
                      <span className="text-[10px] text-gray-400">
                        {l.total_days}d
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 6. Team Roll Call — collapsible member card grid
// ---------------------------------------------------------------------------

function MemberCard({ member, onViewIndividual }) {
  const isWorker = member.classification === 'worker';
  const hasAlert =
    member.attendance_exception ||
    (member.rest_day_compliance && member.rest_day_compliance !== 'compliant') ||
    (isWorker && member.ot_compliance && member.ot_compliance !== 'compliant');

  const displayHours = member.effective_hours ?? member.total_hours;

  const hasClock = !!(member.first_clock_in || member.last_clock_out);
  const hasScheduleCredited = !member.first_clock_in && displayHours != null;
  const showChips =
    member.attendance_exception ||
    (member.rest_day_compliance && member.rest_day_compliance !== 'compliant') ||
    (isWorker && member.ot_compliance && member.ot_compliance !== 'compliant') ||
    member.pending_leave_count > 0;

  const viewDetailBtn = (
    <button
      onClick={() => onViewIndividual(member.staff_id)}
      className="icon-button flex-shrink-0"
      title="View Detail"
      aria-label="View Detail"
    >
      <ArrowRight className="w-4 h-4" />
    </button>
  );

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-shadow p-4 ${
        hasAlert ? 'border-l-4 border-l-red-400 border-t border-r border-b' : 'border-gray-200'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 text-sm truncate">{member.full_name}</p>
          <p className="text-[11px] text-gray-400 font-medium mt-0.5 tabular-nums">{member.staff_id}</p>
        </div>
        <StatusBadge
          status={member.composite_status}
          leaveTypeAbbr={member.leave_type_abbreviation}
          leaveTypeName={member.leave_type_name}
        />
      </div>

      {/* Clock row — button inline when this is the last row */}
      {hasClock && (
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500 mb-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3 flex-shrink-0" />
              {member.first_clock_in ? formatTimeString12Hour(member.first_clock_in) : '—'}
              {' → '}
              {member.last_clock_out ? formatTimeString12Hour(member.last_clock_out) : '…'}
            </span>
            {displayHours != null && (
              <span className="text-gray-700 font-medium">{formatHoursMinutes(displayHours)}</span>
            )}
            {member.hours_source === 'schedule' && (
              <span className="text-[10px] text-indigo-500 font-medium">sched</span>
            )}
            {member.hours_source === 'mixed' && (
              <span className="text-[10px] text-indigo-500 font-medium">mixed</span>
            )}
            {member.beyond_schedule_hours > 0 && (
              <span className={`font-medium ${isWorker ? 'text-blue-700' : 'text-amber-700'}`}>
                +{formatHoursMinutes(member.beyond_schedule_hours)} {isWorker ? 'OT' : 'ext'}
              </span>
            )}
          </div>
          {!showChips && viewDetailBtn}
        </div>
      )}

      {/* Schedule-credited row — button inline when this is the last row */}
      {hasScheduleCredited && (
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500 mb-2">
          <div className="flex items-center gap-2">
            <Timer className="w-3 h-3 flex-shrink-0" />
            <span className="text-gray-700 font-medium">{formatHoursMinutes(displayHours)}</span>
            <span className="text-[10px] text-indigo-500 font-medium">schedule-credited</span>
          </div>
          {!hasClock && !showChips && viewDetailBtn}
        </div>
      )}

      {/* Declared OT */}
      {isWorker && member.declared_ot_hours != null && member.declared_ot_hours > 0 && (
        <div className="text-xs text-blue-600 font-medium mb-2">
          Declared OT: {formatHoursMinutes(member.declared_ot_hours)}
        </div>
      )}

      {/* Compliance chips — button inline at end */}
      {showChips && (
        <div className="flex items-end gap-2">
          <div className="flex flex-wrap gap-1 flex-1">
            {member.attendance_exception === 'absent_no_leave' && (
              <ComplianceChip label="Unexplained Absence" severity="non_compliant" />
            )}
            {member.attendance_exception === 'missing_clockout' && (
              <ComplianceChip label="Missing Clock-out" severity="warning" />
            )}
            {member.rest_day_compliance && member.rest_day_compliance !== 'compliant' && (
              <ComplianceChip label="Rest Day" severity={member.rest_day_compliance} />
            )}
            {isWorker && member.ot_compliance && member.ot_compliance !== 'compliant' && (
              <ComplianceChip label="OT Limit" severity={member.ot_compliance} />
            )}
            {member.pending_leave_count > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-600/20">
                {member.pending_leave_count} leave pending
              </span>
            )}
          </div>
          {viewDetailBtn}
        </div>
      )}

      {/* Fallback — on leave / off day / no data rows at all */}
      {!hasClock && !hasScheduleCredited && !showChips && (
        <div className="flex justify-end">
          {viewDetailBtn}
        </div>
      )}
    </div>
  );
}

function TeamRollCall({ members, onViewIndividual }) {
  const [isOpen, setIsOpen] = useState(true);

  const sortedMembers = useMemo(() => {
    const priority = {
      unaccounted: 0, on_site: 1, work_from_home: 2, external_activity: 3,
      nightwork: 4, on_course: 5, on_leave: 6, nightwork_rest_day: 7,
      non_working_day: 8, public_holiday: 9,
    };
    return [...members].sort(
      (a, b) =>
        (priority[a.composite_status] ?? 99) - (priority[b.composite_status] ?? 99) ||
        a.full_name.localeCompare(b.full_name)
    );
  }, [members]);

  return (
    <AccordionSection
      expanded={isOpen}
      onToggle={() => setIsOpen((v) => !v)}
      theme="employee"
      contentVariant="padded"
      icon={<Users className="w-4 h-4 text-gray-500" />}
      iconBg="bg-gray-100"
      title={<>Team Roll Call <CountBadge count={members.length} theme="employee" size="md" className="ml-1" /></>}
    >
      {sortedMembers.length === 0 ? (
        <p className="text-center py-6 text-sm text-gray-400">No data available</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {sortedMembers.map((m) => (
            <MemberCard key={m.staff_id} member={m} onViewIndividual={onViewIndividual} />
          ))}
        </div>
      )}
    </AccordionSection>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SCOPE_OPTIONS = [
  { id: 'direct-reports', label: 'Direct Reports', icon: Users,     title: 'Direct Reports' },
  { id: 'department',     label: 'Department',     icon: Building2, title: 'Department' },
];

export default function TeamOverview({
  data, isLoading, error,
  trendsData, leaveInsightsData, upcomingLeaveData,
  onViewIndividual,
  canToggleScope = false,
  scope = 'direct-reports',
  onScopeChange,
}) {
  const members = data?.members ?? [];
  const summary = data?.summary ?? {};
  const dateLabel = data?.date ? formatDate(data.date) : null;
  const total = members.length;

  // True when at least one team member is a worker — drives whether
  // the MOM compliance surfaces (watchlist + KPI cards) render at all.
  // Staff-only teams have zero regulatory exposure to the 72h OT cap
  // or the 12-day rest-day limit, so showing those surfaces would be
  // pure noise for the manager reading the dashboard.
  const hasWorkers = useMemo(
    () => members.some((m) => m.classification === 'worker'),
    [members],
  );

  const memberCountNoun = canToggleScope && scope === 'department'
    ? `${total} department member${total !== 1 ? 's' : ''}`
    : `${total} direct report${total !== 1 ? 's' : ''}`;

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
        <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
        <p className="font-medium">Failed to load team overview</p>
        <p className="text-sm mt-1">{error.response?.data?.detail || error.message}</p>
      </div>
    );
  }

  const isNonWorkingDay = members.length > 0 && members.every(
    (m) => ['non_working_day', 'public_holiday'].includes(m.composite_status)
  );

  return (
    <div className="space-y-4">
      {/* Header — title + scope toggle inline on the same row */}
      <div>
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Dashboard Overview</h3>
          {canToggleScope && (
            <>
              <div className="md:hidden">
                <PillToggle
                  value={scope}
                  onChange={onScopeChange}
                  options={SCOPE_OPTIONS}
                  variant="icon"
                  theme="employee"
                  ariaLabel="Team scope"
                />
              </div>
              <div className="hidden md:block">
                <PillToggle
                  value={scope}
                  onChange={onScopeChange}
                  options={SCOPE_OPTIONS}
                  variant="text"
                  theme="employee"
                  className="flex-shrink-0"
                  ariaLabel="Team scope"
                />
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {dateLabel && <p className="text-sm text-gray-500">{dateLabel}</p>}
          <span className="text-sm text-gray-400">&middot;</span>
          <span className="text-sm text-gray-500">{memberCountNoun}</span>
        </div>
      </div>

      {isNonWorkingDay ? (
        <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
          <Palmtree className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Non-working day</p>
          <p className="text-sm text-gray-400 mt-1">
            All team members are off today. Upcoming leave and wellbeing signals are shown below.
          </p>
        </div>
      ) : (
        <>
          {/* 1. Action Center */}
          <ActionCenter members={members} />

          {/* 2. Compliance Watchlist — only when team has workers.
              MOM 72h OT (Part IV) + 12-day rest day (s36) apply to
              workers only. Rendering for staff-only teams would be
              a perpetual empty state — noise, not signal. */}
          {hasWorkers && (
            <ComplianceWatchlist
              members={members}
              onViewIndividual={onViewIndividual}
            />
          )}

          {/* 3. KPI Cards */}
          <KPICards members={members} trendsData={trendsData} hasWorkers={hasWorkers} />

          {/* 4. Status Distribution */}
          <TeamStatusBar summary={summary} total={total} />
        </>
      )}

      {/* 5 + 6: Wellbeing & Upcoming Leave — side by side on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WellbeingFlags leaveInsightsData={leaveInsightsData} trendsData={trendsData} />
        <UpcomingLeavePanel upcomingLeaveData={upcomingLeaveData} />
      </div>

      {/* 7. Team Roll Call */}
      <TeamRollCall members={members} onViewIndividual={onViewIndividual} />
    </div>
  );
}
